import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { createSHA256 } from "hash-wasm";
import { normalizeRelativePath, parentPathOf, type RootRecord } from "@focalfolio/domain-model";
import type {
  CopyVerification,
  FileEntry,
  FileReference,
  FileStat,
  TrashResult,
  WorkspaceFileSystem
} from "@focalfolio/fs-contract";

type PermissionMode = "read" | "readwrite";
type KnownDirectory = "desktop" | "documents" | "downloads" | "music" | "pictures" | "videos";
type PermissionStateValue = "granted" | "denied" | "prompt";
type PermissionCapableHandle = FileSystemHandle & {
  queryPermission?: (descriptor?: { mode?: PermissionMode }) => Promise<PermissionStateValue>;
  requestPermission?: (descriptor?: { mode?: PermissionMode }) => Promise<PermissionStateValue>;
  isSameEntry?: (other: FileSystemHandle) => Promise<boolean>;
};

type DirectoryHandleWithEntries = FileSystemDirectoryHandle & {
  entries: () => AsyncIterableIterator<[string, FileSystemHandle]>;
};

interface HandleDb extends DBSchema {
  handles: {
    key: string;
    value: {
      key: string;
      rootId: string;
      name: string;
      handle: FileSystemDirectoryHandle;
      addedAt: number;
    };
  };
}

const HANDLE_DB_NAME = "focalfolio-handles-v1";
const HANDLE_STORE = "handles";
let handleDbPromise: Promise<IDBPDatabase<HandleDb>> | undefined;

function handleDb(): Promise<IDBPDatabase<HandleDb>> {
  handleDbPromise ??= openDB<HandleDb>(HANDLE_DB_NAME, 1, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(HANDLE_STORE)) {
        database.createObjectStore(HANDLE_STORE, { keyPath: "key" });
      }
    }
  });
  return handleDbPromise;
}

function randomId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function filesystemAccessSupported(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function queryHandlePermission(
  handle: FileSystemHandle,
  mode: PermissionMode = "readwrite"
): Promise<PermissionStateValue> {
  const capable = handle as PermissionCapableHandle;
  if (!capable.queryPermission) return "granted";
  return capable.queryPermission({ mode });
}

export async function requestHandlePermission(
  handle: FileSystemHandle,
  mode: PermissionMode = "readwrite"
): Promise<PermissionStateValue> {
  const capable = handle as PermissionCapableHandle;
  const current = await queryHandlePermission(handle, mode);
  if (current === "granted" || !capable.requestPermission) return current;
  return capable.requestPermission({ mode });
}

async function handlesEqual(a: FileSystemHandle, b: FileSystemHandle): Promise<boolean> {
  const capable = a as PermissionCapableHandle;
  if (!capable.isSameEntry) return a.name === b.name && a.kind === b.kind;
  try {
    return await capable.isSameEntry(b);
  } catch {
    return false;
  }
}

export interface RegisteredRoot {
  record: RootRecord;
  handle: FileSystemDirectoryHandle;
}

export async function registerDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<RegisteredRoot> {
  const database = await handleDb();
  const existing = await database.getAll(HANDLE_STORE);
  for (const candidate of existing) {
    if (await handlesEqual(handle, candidate.handle)) {
      const permission = await queryHandlePermission(candidate.handle);
      return {
        record: {
          id: candidate.rootId,
          name: candidate.name,
          handleKey: candidate.key,
          addedAt: candidate.addedAt,
          permission
        },
        handle: candidate.handle
      };
    }
  }

  const rootId = randomId("root");
  const key = randomId("handle");
  const addedAt = Date.now();
  await database.put(HANDLE_STORE, { key, rootId, name: handle.name, handle, addedAt });
  const permission = await queryHandlePermission(handle);
  return {
    record: { id: rootId, name: handle.name, handleKey: key, addedAt, permission },
    handle
  };
}

export async function pickDirectoryRoot(options: {
  mode?: PermissionMode;
  id?: string;
  startIn?: KnownDirectory | FileSystemHandle;
} = {}): Promise<RegisteredRoot> {
  if (!filesystemAccessSupported()) {
    throw new Error("This browser does not support persistent local folder access.");
  }
  const picker = (window as unknown as Window & { showDirectoryPicker: (options?: unknown) => Promise<FileSystemDirectoryHandle> }).showDirectoryPicker as (options?: {
    id?: string;
    mode?: "read" | "readwrite";
    startIn?: KnownDirectory | FileSystemHandle;
  }) => Promise<FileSystemDirectoryHandle>;
  const handle = await picker({
    id: options.id ?? "focalfolio-library",
    mode: options.mode ?? "readwrite",
    ...(options.startIn ? { startIn: options.startIn } : {})
  });
  const permission = await requestHandlePermission(handle, options.mode ?? "readwrite");
  if (permission !== "granted") throw new Error("Folder permission was not granted.");
  return registerDirectoryHandle(handle);
}

export async function restoreRegisteredRoots(): Promise<RegisteredRoot[]> {
  const database = await handleDb();
  const stored = await database.getAll(HANDLE_STORE);
  return Promise.all(
    stored.map(async (item) => ({
      record: {
        id: item.rootId,
        name: item.name,
        handleKey: item.key,
        addedAt: item.addedAt,
        permission: await queryHandlePermission(item.handle)
      },
      handle: item.handle
    }))
  );
}

export async function forgetRegisteredRoot(rootId: string): Promise<void> {
  const database = await handleDb();
  const items = await database.getAll(HANDLE_STORE);
  const item = items.find((candidate) => candidate.rootId === rootId);
  if (item) await database.delete(HANDLE_STORE, item.key);
}

function validateFilename(filename: string): void {
  if (!filename || filename === "." || filename === "..") throw new Error("Filename is empty or reserved.");
  if (/[\\/:*?"<>|\u0000-\u001f]/u.test(filename)) throw new Error(`Filename contains unsupported characters: ${filename}`);
  if (/[. ]$/u.test(filename)) throw new Error("Filenames ending in a dot or space are not portable.");
}

async function streamToWritable(
  data: Blob | Uint8Array | ReadableStream<Uint8Array>,
  writable: FileSystemWritableFileStream
): Promise<void> {
  try {
    if (data instanceof Blob) {
      await data.stream().pipeTo(writable);
      return;
    }
    if (data instanceof Uint8Array) {
      const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
      await writable.write(buffer);
      await writable.close();
      return;
    }
    await data.pipeTo(writable);
  } catch (error) {
    try {
      await writable.abort(error);
    } catch {
      // The original write error is more useful than an abort failure.
    }
    throw error;
  }
}

async function digestFile(file: File): Promise<string> {
  const hasher = await createSHA256();
  const reader = file.stream().getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      hasher.update(value);
    }
    return hasher.digest();
  } finally {
    reader.releaseLock();
  }
}

export class BrowserWorkspaceFileSystem implements WorkspaceFileSystem {
  readonly roots = new Map<string, FileSystemDirectoryHandle>();

  constructor(initialRoots: RegisteredRoot[] = []) {
    for (const root of initialRoots) this.roots.set(root.record.id, root.handle);
  }

  register(rootId: string, handle: FileSystemDirectoryHandle): void {
    this.roots.set(rootId, handle);
  }

  unregister(rootId: string): void {
    this.roots.delete(rootId);
  }

  private root(rootId: string): FileSystemDirectoryHandle {
    const handle = this.roots.get(rootId);
    if (!handle) throw new Error(`The local root ${rootId} is not currently linked.`);
    return handle;
  }

  private async resolveDirectory(
    rootId: string,
    relativePath = "",
    options: { create?: boolean } = {}
  ): Promise<FileSystemDirectoryHandle> {
    let current = this.root(rootId);
    const normalized = normalizeRelativePath(relativePath);
    if (!normalized) return current;
    for (const part of normalized.split("/")) {
      current = await current.getDirectoryHandle(part, { create: options.create ?? false });
    }
    return current;
  }

  private async resolveFileHandle(reference: FileReference, create = false): Promise<FileSystemFileHandle> {
    const normalized = normalizeRelativePath(reference.relativePath);
    const filename = normalized.split("/").at(-1);
    if (!filename) throw new Error("A file path is required.");
    const directory = await this.resolveDirectory(reference.rootId, parentPathOf(normalized), { create });
    return directory.getFileHandle(filename, { create });
  }

  async *list(rootId: string, relativePath = "", signal?: AbortSignal): AsyncIterable<FileEntry> {
    const directory = (await this.resolveDirectory(rootId, relativePath)) as DirectoryHandleWithEntries;
    for await (const [name, handle] of directory.entries()) {
      if (signal?.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
      const path = normalizeRelativePath([relativePath, name].filter(Boolean).join("/"));
      if (handle.kind === "directory") {
        yield { kind: "directory", name, relativePath: path, size: 0, lastModified: 0, mimeType: "" };
      } else {
        const file = await (handle as FileSystemFileHandle).getFile();
        yield {
          kind: "file",
          name,
          relativePath: path,
          size: file.size,
          lastModified: file.lastModified,
          mimeType: file.type
        };
      }
    }
  }

  async *scan(
    rootId: string,
    options: { includeHidden?: boolean; signal?: AbortSignal } = {}
  ): AsyncIterable<FileEntry> {
    const queue = [""];
    while (queue.length > 0) {
      if (options.signal?.aborted) throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
      const directory = queue.shift() ?? "";
      for await (const entry of this.list(rootId, directory, options.signal)) {
        const hidden = entry.name.startsWith(".");
        if (entry.kind === "directory") {
          if (entry.name === ".focalfolio-trash") continue;
          if (!hidden || options.includeHidden) queue.push(entry.relativePath);
        } else if (!hidden || options.includeHidden) {
          yield entry;
        }
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }

  async stat(reference: FileReference): Promise<FileStat> {
    const file = await this.getFile(reference);
    return { kind: "file", name: file.name, size: file.size, lastModified: file.lastModified, mimeType: file.type };
  }

  async getFile(reference: FileReference): Promise<File> {
    return (await this.resolveFileHandle(reference)).getFile();
  }

  async readSlice(reference: FileReference, start: number, length: number): Promise<Uint8Array> {
    const file = await this.getFile(reference);
    return new Uint8Array(await file.slice(start, start + length).arrayBuffer());
  }

  async writeFile(
    reference: FileReference,
    data: Blob | Uint8Array | ReadableStream<Uint8Array>,
    options: { overwrite?: boolean } = {}
  ): Promise<void> {
    const normalized = normalizeRelativePath(reference.relativePath);
    const filename = normalized.split("/").at(-1);
    if (!filename) throw new Error("A destination filename is required.");
    validateFilename(filename);
    const directory = await this.resolveDirectory(reference.rootId, parentPathOf(normalized), { create: true });
    if (!options.overwrite) {
      try {
        await directory.getFileHandle(filename);
        throw new Error(`Destination already exists: ${normalized}`);
      } catch (error) {
        if (error instanceof DOMException && error.name === "NotFoundError") {
          // Expected: destination does not exist.
        } else {
          throw error;
        }
      }
    }
    const handle = await directory.getFileHandle(filename, { create: true });
    const writable = await handle.createWritable({ keepExistingData: false });
    await streamToWritable(data, writable);
  }

  async copyVerified(
    source: FileReference,
    destination: FileReference,
    options: { overwrite?: boolean; digest?: boolean } = {}
  ): Promise<CopyVerification> {
    const sourceFile = await this.getFile(source);
    await this.writeFile(destination, sourceFile.stream(), { overwrite: options.overwrite });
    const destinationFile = await this.getFile(destination);
    const sizeVerified = sourceFile.size === destinationFile.size;
    if (!sizeVerified) {
      await this.removePermanently(destination).catch(() => undefined);
      throw new Error(`Copy verification failed for ${destination.relativePath}: byte counts differ.`);
    }
    let digest: string | undefined;
    if (options.digest) {
      const [sourceDigest, destinationDigest] = await Promise.all([digestFile(sourceFile), digestFile(destinationFile)]);
      if (sourceDigest !== destinationDigest) {
        await this.removePermanently(destination).catch(() => undefined);
        throw new Error(`Copy verification failed for ${destination.relativePath}: SHA-256 digests differ.`);
      }
      digest = sourceDigest;
    }
    return {
      sourceSize: sourceFile.size,
      destinationSize: destinationFile.size,
      verified: true,
      ...(digest ? { digest } : {})
    };
  }

  async move(
    source: FileReference,
    destination: FileReference,
    options: { overwrite?: boolean } = {}
  ): Promise<void> {
    await this.copyVerified(source, destination, { overwrite: options.overwrite, digest: true });
    await this.removePermanently(source);
  }

  async rename(source: FileReference, newFilename: string): Promise<FileReference> {
    validateFilename(newFilename);
    const destination = {
      rootId: source.rootId,
      relativePath: normalizeRelativePath([parentPathOf(source.relativePath), newFilename].filter(Boolean).join("/"))
    };
    if (destination.relativePath.toLocaleLowerCase("en-US") === source.relativePath.toLocaleLowerCase("en-US")) {
      if (destination.relativePath === source.relativePath) return source;
      const temporary = {
        rootId: source.rootId,
        relativePath: normalizeRelativePath(
          [parentPathOf(source.relativePath), `.focalfolio-rename-${crypto.randomUUID()}.tmp`].filter(Boolean).join("/")
        )
      };
      await this.move(source, temporary);
      await this.move(temporary, destination);
      return destination;
    }
    await this.move(source, destination);
    return destination;
  }

  async createDirectory(rootId: string, relativePath: string): Promise<void> {
    await this.resolveDirectory(rootId, relativePath, { create: true });
  }

  async trash(reference: FileReference): Promise<TrashResult> {
    const timestamp = new Date().toISOString().replace(/[:.]/gu, "-");
    const token = crypto.randomUUID();
    const original = normalizeRelativePath(reference.relativePath);
    const trashPath = normalizeRelativePath(`.focalfolio-trash/${timestamp}-${token}/${original}`);
    await this.move(reference, { rootId: reference.rootId, relativePath: trashPath });
    const manifestPath = normalizeRelativePath(`.focalfolio-trash/${timestamp}-${token}/manifest.json`);
    const manifest = new TextEncoder().encode(
      JSON.stringify({ version: 1, rootId: reference.rootId, originalPath: original, trashPath, deletedAt: Date.now() }, null, 2)
    );
    await this.writeFile({ rootId: reference.rootId, relativePath: manifestPath }, manifest, { overwrite: true });
    return { originalPath: original, trashPath, manifestPath };
  }

  async restoreTrash(rootId: string, trashPath: string): Promise<FileReference> {
    const normalized = normalizeRelativePath(trashPath);
    const segments = normalized.split("/");
    if (segments[0] !== ".focalfolio-trash" || segments.length < 4) throw new Error("Invalid FocalFolio Trash path.");
    const restoredPath = segments.slice(2).join("/");
    const source = { rootId, relativePath: normalized };
    const destination = { rootId, relativePath: restoredPath };
    await this.move(source, destination);
    return destination;
  }

  async removePermanently(reference: FileReference): Promise<void> {
    const normalized = normalizeRelativePath(reference.relativePath);
    const name = normalized.split("/").at(-1);
    if (!name) throw new Error("A path is required.");
    const directory = await this.resolveDirectory(reference.rootId, parentPathOf(normalized));
    await directory.removeEntry(name, { recursive: true });
  }
}
